import type { CatalogModelEntry, ModelsCatalogJson } from "@x12i/ai-profiles";
import { computeSupportsReasoning } from "../models/reasoningModel.js";
import type { AiModelPricing, AiModelRecord } from "../models/types.js";
import type { OpenRouterModelApi, OpenRouterPricingApi } from "../models/openrouter.types.js";
import { normalizeProvider, normalizeString } from "../sync/modelNameResolver/normalize.js";
import type { CatalogSourceKind } from "./catalogSources.js";

const TOKENS_PER_UNIT = 1_000_000;

function usdPerToken(perMillion?: number | null): number {
  if (perMillion == null || !Number.isFinite(perMillion)) return 0;
  return perMillion / TOKENS_PER_UNIT;
}

export function canonicalCatalogModelId(provider: string, modelId: string): string {
  const m = normalizeString(modelId);
  if (m.includes("/")) return m;
  const p = normalizeProvider(provider) ?? normalizeString(provider);
  return `${p}/${m}`;
}

function mapStatus(status?: string): AiModelRecord["status"] {
  if (!status) return "active";
  const s = status.toLowerCase();
  if (s === "deprecated" || s === "legacy") return "deprecated";
  if (s === "active" || s === "preview" || s === "announced") return "active";
  return "unknown";
}

function mapModalities(
  modalities?: CatalogModelEntry["modalities"] | CatalogModelEntry["openrouter"]["modalities"],
): { input: string[]; output: string[]; modality: string } {
  if (modalities && typeof modalities === "object" && !Array.isArray(modalities)) {
    const input = modalities.input ?? ["text"];
    const output = modalities.output ?? ["text"];
    return { input, output, modality: `${input.join("+")}->${output.join("+")}` };
  }
  return { input: ["text"], output: ["text"], modality: "text->text" };
}

function pricingToOpenRouterApi(raw: NonNullable<
  CatalogModelEntry["openrouter"]["pricing"]
>): OpenRouterPricingApi {
  const scale = (n?: number) => (n ?? 0) / TOKENS_PER_UNIT;
  return {
    prompt: String(scale(raw.input)),
    completion: String(scale(raw.output)),
    request: "0",
    image: String(scale(raw.imageInput)),
    input_cache_read: raw.cachedInput != null ? String(scale(raw.cachedInput)) : undefined,
    input_cache_write: raw.cacheWrite != null ? String(scale(raw.cacheWrite)) : undefined,
    internal_reasoning:
      raw.internalReasoning != null ? String(scale(raw.internalReasoning)) : undefined,
    web_search:
      raw.webSearchPerRequest != null ? String(raw.webSearchPerRequest) : undefined,
  };
}

function pricingFromCatalog(
  raw: NonNullable<CatalogModelEntry["openrouter"]["pricing"]>,
  pricedAt: string,
  source: AiModelPricing["source"],
): AiModelPricing {
  return {
    promptUsdPerToken: usdPerToken(raw.input),
    completionUsdPerToken: usdPerToken(raw.output),
    imageUsdPerUnit: usdPerToken(raw.imageInput),
    requestUsdPerRequest: 0,
    cacheReadUsdPerToken: usdPerToken(raw.cachedInput),
    cacheWriteUsdPerToken: usdPerToken(raw.cacheWrite),
    reasoningUsdPerToken: usdPerToken(raw.internalReasoning),
    webSearchUsdPerRequest:
      typeof raw.webSearchPerRequest === "number" ? raw.webSearchPerRequest : undefined,
    pricedAt,
    source,
  };
}

function stubOpenRouterApi(
  modelId: string,
  entry: CatalogModelEntry,
  modalities: ReturnType<typeof mapModalities>,
  pricing: OpenRouterPricingApi,
  contextWindow: number,
  maxCompletionTokens: number | null,
): OpenRouterModelApi {
  return {
    id: modelId,
    canonical_slug: entry.openrouter.canonicalSlug ?? modelId,
    name: entry.displayName ?? modelId,
    created: 0,
    description: entry.displayName ?? "",
    context_length: contextWindow,
    architecture: {
      modality: modalities.modality,
      input_modalities: modalities.input,
      output_modalities: modalities.output,
      tokenizer: "",
      instruct_type: null,
    },
    pricing,
    top_provider: {
      context_length: contextWindow,
      max_completion_tokens: maxCompletionTokens,
      is_moderated: false,
    },
    per_request_limits: null,
    supported_parameters: entry.openrouter.capabilities?.toolCalling ? ["tools"] : [],
    default_parameters: null,
  };
}

function directProviderId(entry: CatalogModelEntry): string {
  const direct = entry.direct;
  if (direct.provider === "custom" && direct.vendorNamespace) {
    return normalizeProvider(direct.vendorNamespace) ?? direct.vendorNamespace;
  }
  return normalizeProvider(direct.provider) ?? direct.provider;
}

function buildRecord(
  entry: CatalogModelEntry,
  kind: CatalogSourceKind,
  pricedAt: string,
): AiModelRecord | null {
  const pricingRaw = entry.openrouter.pricing;
  if (!pricingRaw) return null;
  if (pricingRaw.input == null && pricingRaw.output == null) return null;

  const modalities = mapModalities(entry.modalities ?? entry.openrouter.modalities);
  const contextWindow = entry.contextWindow ?? entry.openrouter.contextWindow ?? 0;
  const maxCompletionTokens =
    entry.maxCompletionTokens ?? entry.openrouter.maxCompletionTokens ?? null;

  const orSlug = normalizeString(entry.openrouter.modelId);
  const providerId = directProviderId(entry);
  const bareModelId = normalizeString(entry.direct.modelId);

  const modelId =
    kind === "openrouter"
      ? orSlug
      : canonicalCatalogModelId(providerId, bareModelId);

  const pricingSource: AiModelPricing["source"] =
    kind === "openrouter" ? "openrouter" : "direct";
  const pricing = pricingFromCatalog(pricingRaw, pricedAt, pricingSource);
  const openRouterPricing = pricingToOpenRouterApi(pricingRaw);
  const openRouter = stubOpenRouterApi(
    modelId,
    entry,
    modalities,
    openRouterPricing,
    contextWindow,
    maxCompletionTokens,
  );

  const supportedParameters = openRouter.supported_parameters ?? [];
  const supportsReasoning =
    entry.openrouter.capabilities?.reasoning === true ||
    computeSupportsReasoning({
      supportedParameters,
      pricing,
      openRouterPricing,
    });

  const aliases = new Set<string>();
  aliases.add(bareModelId);
  aliases.add(orSlug);
  if (entry.displayName) aliases.add(entry.displayName);
  const slash = orSlug.indexOf("/");
  if (slash > 0) aliases.add(orSlug.slice(slash + 1));
  if (modelId !== orSlug && modelId.includes("/")) {
    aliases.add(modelId.slice(modelId.indexOf("/") + 1));
  }

  return {
    modelId,
    name: entry.displayName ?? modelId,
    providerId: kind === "openrouter" && orSlug.includes("/")
      ? orSlug.split("/")[0]!
      : providerId,
    canonicalSlug: entry.openrouter.canonicalSlug ?? orSlug,
    status: mapStatus(entry.status ?? entry.openrouter.status),
    description: entry.displayName ?? "",
    created: 0,
    expirationDate: null,
    contextLength: contextWindow,
    maxCompletionTokens,
    isModerated: false,
    modality: modalities.modality,
    inputModalities: modalities.input,
    outputModalities: modalities.output,
    tokenizer: "",
    instructType: null,
    supportedParameters,
    defaultParameters: null,
    perRequestLimits: null,
    pricing,
    openRouterPricing,
    architecture: openRouter.architecture,
    topProvider: openRouter.top_provider,
    openRouter,
    aliases: [...aliases],
    availableOnOpenRouter: Boolean(entry.openrouter.modelId),
    supportsStreaming: modalities.output.includes("text"),
    supportsTools: entry.openrouter.capabilities?.toolCalling === true,
    supportsReasoning,
    primaryOutputModality: modalities.output[0] ?? "text",
    syncedAt: pricedAt,
    syncSource: kind === "openrouter" ? "openrouter" : "manual",
  };
}

/**
 * Split a unified ai-profiles models catalog into direct + OpenRouter record maps.
 */
export function modelsFromAiProfilesCatalog(catalog: ModelsCatalogJson): {
  direct: Map<string, AiModelRecord>;
  openrouter: Map<string, AiModelRecord>;
} {
  const direct = new Map<string, AiModelRecord>();
  const openrouter = new Map<string, AiModelRecord>();
  const pricedAt =
    catalog.generatedAt ??
    catalog.sources?.openrouter?.verifiedAt ??
    new Date().toISOString();

  for (const entry of catalog.models) {
    const directRecord = buildRecord(entry, "direct", pricedAt);
    if (directRecord) direct.set(directRecord.modelId, directRecord);

    if (entry.openrouter.modelId) {
      const orRecord = buildRecord(entry, "openrouter", pricedAt);
      if (orRecord) openrouter.set(orRecord.modelId, orRecord);
    }
  }

  return { direct, openrouter };
}

/** @deprecated Use {@link modelsFromAiProfilesCatalog}. */
export function modelsFromX12iCatalogFile(
  file: ModelsCatalogJson,
  _kind: CatalogSourceKind,
): Map<string, AiModelRecord> {
  const { direct, openrouter } = modelsFromAiProfilesCatalog(file);
  return _kind === "openrouter" ? openrouter : direct;
}
